/**
 * Which choices a dish offers.
 *
 * WHY THIS IS THE PIECE THAT WAS ACTUALLY MISSING
 * -----------------------------------------------
 * A group could be created through the API from the beginning. What could
 * never happen was attaching one to a dish: `product_modifier_groups` had
 * never held a row, because no endpoint could write one. So every group ever
 * created would have sat there offering nothing to nobody.
 *
 * That is why no dish in this system offers a size or an extra — not because
 * none were configured, but because there was nowhere to put them.
 *
 * WHY THE ORDER IS KEPT
 * ---------------------
 * A size is asked for before the extras, because that is the order the
 * question gets asked at a counter. The list is sent as it is shown, and the
 * till asks in the same order.
 */
import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';

import { api } from '../../lib/api.js';
import { Button } from '../../components/ui/Button.jsx';
import { describeRules } from './choiceRules.js';

export function DishChoices({ product, onClose, onSaved }) {
  const all = useQuery({
    queryKey: ['menu', 'modifier-groups'],
    // Only groups in use — a withdrawn one cannot be attached, and offering
    // it here would produce a refusal nobody could explain.
    queryFn: () => api.get('/menu/modifier-groups'),
  });

  const attached = useQuery({
    queryKey: ['menu', 'product-modifier-groups', product.id],
    queryFn: () => api.get(`/menu/products/${product.id}/modifier-groups`),
  });

  /**
   * What is saved, and what has been changed to.
   *
   * Derived rather than copied into state when the query lands. Setting state
   * during render does not work — React finishes the current render first and
   * the markup reads a value that is not there yet, which is a crash this
   * codebase has already had once on the recipe form.
   */
  const saved = useMemo(() => (attached.data?.groups ?? []).map((g) => g.id), [attached.data]);

  const [edited, setEdited] = useState(null);
  const chosen = edited ?? saved;

  // Memoised so the lookup below is not rebuilt on every keystroke: the `??`
  // makes a fresh array each render, which would defeat it.
  const groups = useMemo(() => all.data?.groups ?? [], [all.data]);
  const byId = useMemo(() => new Map(groups.map((g) => [g.id, g])), [groups]);

  const save = useMutation({
    mutationFn: () => api.put(`/menu/products/${product.id}/modifier-groups`, { groupIds: chosen }),
    onSuccess: onSaved,
  });

  const loaded = all.data && attached.data;

  const toggle = (id) =>
    setEdited(chosen.includes(id) ? chosen.filter((g) => g !== id) : [...chosen, id]);

  const move = (index, by) => {
    const next = [...chosen];
    const to = index + by;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    setEdited(next);
  };

  if (!loaded) {
    return (
      <div className="mb-5 rounded-xl border border-rule bg-surface p-5 text-sm text-ink-3">
        Loading the choices…
      </div>
    );
  }

  return (
    <div className="mb-5 rounded-xl border border-rule bg-surface p-5">
      <h3 className="mb-1 font-medium text-ink">{product.name}</h3>
      <p className="mb-4 text-sm text-ink-3">
        What the till asks for when somebody orders this, in the order it asks.
      </p>

      {groups.length === 0 ? (
        <p className="rounded-lg bg-surface-3 px-3.5 py-2.5 text-sm text-ink-2">
          There are no choice groups to attach yet. Make one on the Choices tab first.
        </p>
      ) : (
        <>
          {chosen.length > 0 && (
            <ol className="mb-4 space-y-2">
              {chosen.map((id, index) => {
                const group = byId.get(id);
                if (!group) return null;

                return (
                  <li
                    key={id}
                    className="flex flex-wrap items-center gap-3 rounded-lg border border-rule bg-surface-3 px-3 py-2"
                  >
                    <span className="w-5 text-sm tabular-nums text-ink-3">{index + 1}</span>
                    <span className="flex-1 text-sm text-ink">{group.name}</span>
                    <span className="text-xs text-ink-3">{describeRules(group)}</span>

                    <span className="flex gap-1">
                      <button
                        type="button"
                        onClick={() => move(index, -1)}
                        disabled={index === 0}
                        aria-label={`Ask for ${group.name} sooner`}
                        className="px-1.5 text-sm text-ink-3 hover:text-ink disabled:opacity-30"
                      >
                        ↑
                      </button>
                      <button
                        type="button"
                        onClick={() => move(index, 1)}
                        disabled={index === chosen.length - 1}
                        aria-label={`Ask for ${group.name} later`}
                        className="px-1.5 text-sm text-ink-3 hover:text-ink disabled:opacity-30"
                      >
                        ↓
                      </button>
                    </span>

                    <button
                      type="button"
                      onClick={() => toggle(id)}
                      className="text-sm text-ink-3 underline hover:text-bad"
                    >
                      Remove
                    </button>
                  </li>
                );
              })}
            </ol>
          )}

          <div className="space-y-1.5">
            {groups
              .filter((g) => !chosen.includes(g.id))
              .map((group) => (
                <label key={group.id} className="flex items-center gap-2.5 text-sm text-ink-2">
                  <input
                    type="checkbox"
                    aria-label={`Offer ${group.name}`}
                    checked={false}
                    onChange={() => toggle(group.id)}
                    className="h-4 w-4"
                  />
                  <span className="text-ink">{group.name}</span>
                  <span className="text-xs text-ink-3">{describeRules(group)}</span>
                  <span className="text-xs text-ink-3">
                    {(group.options ?? []).map((o) => o.name).join(', ')}
                  </span>
                </label>
              ))}
          </div>
        </>
      )}

      {save.isError && (
        <p className="mt-3 text-sm text-bad">{save.error?.message ?? 'That did not save.'}</p>
      )}

      <div className="mt-5 flex gap-2">
        <Button onClick={() => save.mutate()} disabled={save.isPending || groups.length === 0}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
}
